import React, { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'

const TOOLTIP_MAX_WIDTH = 300
const TOOLTIP_GAP = 6

interface Props {
  description: string
  testId?: string
}

/**
 * Affiche les 3 premières lignes non-vides de la description.
 * Le tooltip est rendu via un portal (attaché à document.body) pour
 * bypasser tout overflow:hidden parent, et positionné par manipulation
 * directe du DOM (aucun setState → aucun re-rendu → aucune vibration).
 */
const OptionDescription: React.FC<Props> = ({ description, testId }) => {
  const btnRef = useRef<HTMLButtonElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const openRef = useRef(false)

  const lines = description.split(/\r?\n/)

  // 3 lignes non-vides pour le preview
  let nonEmptyCount = 0
  let cutIndex = lines.length
  for (let i = 0; i < lines.length; i++) {
    if (lines[i].trim() !== '') {
      nonEmptyCount++
      if (nonEmptyCount === 3) {
        cutIndex = i + 1
        break
      }
    }
  }
  const hasMore = cutIndex < lines.length
  const preview = lines.slice(0, cutIndex).join('\n')

  const positionAndShow = () => {
    const el = tooltipRef.current
    const btn = btnRef.current
    if (!el || !btn) return

    // Afficher temporairement pour mesurer la hauteur réelle
    el.style.visibility = 'hidden'
    el.style.display = 'block'

    const btnRect = btn.getBoundingClientRect()
    const { height } = el.getBoundingClientRect()
    const vh = window.innerHeight
    const vw = window.innerWidth

    // Horizontal : aligner avec le bouton, recaler si dépassement à droite
    let left = btnRect.left
    if (left + TOOLTIP_MAX_WIDTH > vw - 8) {
      left = Math.max(8, vw - TOOLTIP_MAX_WIDTH - 8)
    }

    // Vertical : au-dessus si la place est suffisante, sinon en dessous
    const spaceAbove = btnRect.top - TOOLTIP_GAP
    let top: number
    if (spaceAbove >= height) {
      top = btnRect.top - TOOLTIP_GAP - height
    } else {
      top = Math.min(btnRect.bottom + TOOLTIP_GAP, vh - height - 8)
    }

    el.style.top = `${top}px`
    el.style.left = `${left}px`
    el.style.visibility = 'visible'
  }

  const hide = () => {
    const el = tooltipRef.current
    if (el) el.style.display = 'none'
  }

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    openRef.current = !openRef.current
    if (openRef.current) {
      positionAndShow()
    } else {
      hide()
    }
  }

  useEffect(() => {
    const close = () => {
      openRef.current = false
      hide()
    }
    document.addEventListener('click', close)
    return () => document.removeEventListener('click', close)
  }, [])

  return (
    <div
      data-testid={testId}
      style={{ fontSize: '.8rem', color: '#9ca3af', marginTop: '.35rem', lineHeight: 1.4 }}
    >
      <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
        {preview}
        {hasMore && '…'}
      </p>
      {hasMore && (
        <>
          <button
            ref={btnRef}
            type="button"
            onClick={handleToggle}
            aria-label="Voir la description complète"
            style={{
              background: 'none',
              border: 'none',
              color: '#60a5fa',
              cursor: 'pointer',
              padding: '0 0.2rem',
              fontSize: '.75rem',
              verticalAlign: 'middle',
            }}
          >
            ⓘ
          </button>
          {createPortal(
            <div
              ref={tooltipRef}
              role="tooltip"
              style={{
                display: 'none',
                position: 'fixed',
                top: 0,
                left: 0,
                background: '#1e293b',
                color: '#e2e8f0',
                padding: '0.5rem 0.75rem',
                borderRadius: '0.375rem',
                whiteSpace: 'pre-wrap',
                fontSize: '.8rem',
                lineHeight: 1.5,
                zIndex: 99999,
                width: `${TOOLTIP_MAX_WIDTH}px`,
                maxHeight: '60vh',
                overflowY: 'auto',
                boxShadow: '0 4px 6px -1px rgba(0,0,0,0.5)',
                border: '1px solid rgba(148,163,184,0.2)',
                pointerEvents: 'auto',
              }}
              onClick={e => e.stopPropagation()}
            >
              {description}
            </div>,
            document.body
          )}
        </>
      )}
    </div>
  )
}

export default OptionDescription
